import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {RiskAssessmentCodeRequest} from 'src/api/ProactApiClient';
import {EntityIdentifierEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {
  useCreateRiskAssessmentCodeMutation,
  useGetRiskAssessmentCodeQuery,
  useUpdateRiskAssessmentCodeMutation,
} from 'src/redux/queries/riskAsessmentCode';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SidebarLinkRiskAssessment} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2} from 'src/types/FormV2';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {
  MapRiskAssessmentResponseToFormData,
  RiskAssessmentCodeAreaPillConfig,
  racDocumentTitle,
} from '../riskAssessmentHelpers';
import {RiskAssessmentContentTab} from './RiskAssessmentContentTab';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const DEFAULT_FORM_DATA: IFormDataV2 = {
  name: 'New Risk Assessment Code',
  area: '',
  lowName: 'Low',
  lowMinRange: 1,
  lowMaxRange: 9,
  midName: 'Mid',
  midMinRange: 10,
  midMaxRange: 20,
  highName: 'High',
  highMinRange: 21,
  highMaxRange: 25,
  probability_1: 'Very Unlikely (has not happened before)',
  probability_2: 'Slight (rarely occurs)',
  probability_3: 'Possible (not common)',
  probability_4: 'Likely (has happened before, can happen again)',
  probability_5: 'Very Likely (occurs frequently)',
  severity_1: 'Near Miss (no injury or property damage)',
  severity_2:
    'First Aid Incident (basic first aid, OTC medications) / Property Damage (no repairs needed)',
  severity_3:
    'Recordable Incident (advanced care required, i.e. stitches, prescriptions) / Partial Loss of property (work is not interrupted)',
  severity_4:
    'Lost Time Incident (days away from work) / Partial Loss of Property (work is interrupted)',
  severity_5: 'Fatality/Total Loss of Property',
};

const navigationConfig: PageNavigationConfig = {
  entityName: 'Risk Assessment Code',
  entityNamePlural: 'Risk Assessment Codes',
  listPath: PathUtility.RiskAssessment(),
  viewPath: id => PathUtility.RiskAssessmentView(id, 'content'),
};

export type RiskAssessmentCodeViewType = 'content' | 'audit' | '' | ':view';

export interface RiskAssessmentFormProps {
  id?: number;
  view?: string;
  mode: 'create' | 'view' | 'edit';
}

export const RiskAssessmentForm: React.FC<RiskAssessmentFormProps> = ({
  id,
  view,
  mode,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);
  useSetDocumentTitle(racDocumentTitle);

  const perms = useModulePermissions(ModuleEnum.RiskAssessmentCodes);
  const auditPerms = useAuditLogPermissions();

  const [formData, setFormData] = useState<IFormDataV2>(DEFAULT_FORM_DATA);
  const validationRef = useRef<(() => boolean) | undefined>();
  const matrixValidationRef = useRef<(() => boolean) | undefined>();

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const {
    data: fetchedData,
    isLoading,
    error: fetchError,
  } = useGetRiskAssessmentCodeQuery(id!, {
    skip: id === undefined,
  });

  useEffect(() => {
    if (fetchError) {
      setAlert(parseErrorMessage(fetchError), 'error');
    }
  }, [fetchError, setAlert]);

  useEffect(() => {
    if (id === undefined) {
      setFormData({...DEFAULT_FORM_DATA});
      return;
    }
    if (fetchedData) {
      setFormData(MapRiskAssessmentResponseToFormData(fetchedData));
    }
  }, [id, fetchedData]);

  const [createRiskAssessmentCode] = useCreateRiskAssessmentCodeMutation();
  const [updateRiskAssessmentCode] = useUpdateRiskAssessmentCodeMutation();

  const handleSave = async () => {
    try {
      setIsSavePending(true);

      const body = {
        ...formData,
        lowMinRange: 1,
        highMaxRange: 25,
        severity: {
          1: formData.severity_1,
          2: formData.severity_2,
          3: formData.severity_3,
          4: formData.severity_4,
          5: formData.severity_5,
        },
        probability: {
          1: formData.probability_1,
          2: formData.probability_2,
          3: formData.probability_3,
          4: formData.probability_4,
          5: formData.probability_5,
        },
      } as RiskAssessmentCodeRequest;

      const res =
        id === undefined
          ? await createRiskAssessmentCode(body).unwrap()
          : await updateRiskAssessmentCode(body).unwrap();

      setAlert('Risk assessment code was successfully saved', 'success');

      navigate(PathUtility.RiskAssessmentView(String(res.id), 'content'), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = () => {
    const formValid = validationRef.current ? validationRef.current() : true;
    const matrixValid = matrixValidationRef.current
      ? matrixValidationRef.current()
      : true;

    if (formValid && matrixValid) {
      void handleSave();
    }
  };

  const handleChange = useCallback((name: string, value: unknown) => {
    setFormData(prev => ({...prev, [name]: value}));
  }, []);

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.RiskAssessmentView(id, 'content')
      : PathUtility.RiskAssessment()
  );

  const contentTab = (
    <RiskAssessmentContentTab
      formData={formData}
      onChange={handleChange}
      mode={mode}
      validationFnRef={isViewMode ? undefined : validationRef}
      matrixValidationFnRef={isViewMode ? undefined : matrixValidationRef}
    />
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<RiskAssessmentCodeViewType>({
    view: (view ?? 'content') as RiskAssessmentCodeViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: contentTab,
      },
      {
        view: 'audit',
        label: 'Audit',
        hidden: !isViewMode,
        disabled: !auditPerms.view,
        component: id ? (
          <AuditModule
            entityId={String(id)}
            entityType={EntityIdentifierEnum.RiskAssessmentCode}
          />
        ) : null,
      },
    ],
    onSelectView: view =>
      navigate(
        PathUtility.RiskAssessmentView(
          String(id),
          view as RiskAssessmentCodeViewType
        ),
        {replace: true}
      ),
  });

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: formData?.name,
      }),
    [mode, id, formData?.name]
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkRiskAssessment.icon}
          subtitle={formData?.internalIdentifier}>
          {isCreateMode
            ? 'New Risk Assessment Code'
            : formData?.name ?? 'Risk Assessment Code'}
        </PageHeader.Title>

        {isViewMode && formData?.area && (
          <PageHeader.Badges>
            <PageHeader.Badge
              color={RiskAssessmentCodeAreaPillConfig(formData.area).color}
              variant="light">
              {RiskAssessmentCodeAreaPillConfig(formData.area).label}
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        {!isCreateMode && formData?.createdAt && (
          <PageHeader.Meta>
            <PageHeader.MetaItem label="Created">
              {new Date(formData.createdAt as string).toLocaleDateString(
                undefined,
                {year: 'numeric', month: 'short', day: 'numeric'}
              )}
            </PageHeader.MetaItem>
          </PageHeader.Meta>
        )}

        <PageHeader.Actions>
          {isViewMode && perms.update && (
            <PageHeader.Action
              variant="edit"
              onClick={() => navigate(PathUtility.RiskAssessmentEdit(+id!))}>
              Edit
            </PageHeader.Action>
          )}

          {!isViewMode && (
            <>
              <PageHeader.Action variant="cancel" onClick={handleCancel}>
                Cancel
              </PageHeader.Action>
              <PageHeader.Action
                variant={isCreateMode ? 'create' : 'save'}
                onClick={handleValidateAndSave}
                loading={isSavePending}>
                {isCreateMode ? 'Create' : 'Save Changes'}
              </PageHeader.Action>
            </>
          )}
        </PageHeader.Actions>
      </PageHeader>

      {RenderedTabsComponent}

      {!isViewMode && (
        <Group>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create' : 'Save Changes'}
          </PageHeader.Action>
        </Group>
      )}
    </Stack>
  );
};
